import {
  DEFAULT_LANGUAGE,
  FALLBACK_LANGUAGE,
  type SupportedLanguage,
  isLanguageSupported,
} from '../config';
import { loadTranslation } from '../translations-loader';
import type { I18nInstance, TranslationFunction } from '../types';

/**
 * Custom backend for loading bundled translations with code-splitting.
 * Caches per-namespace per-language; Vite/Metro both honour the dynamic
 * import() inside loadTranslation for code-splitting.
 */
class BundledTranslationBackend {
  private cache: Map<string, any> = new Map();
  private loadingPromises: Map<string, Promise<any>> = new Map();

  async read(language: string, namespace: string): Promise<any> {
    const cacheKey = `${language}:${namespace}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey);
    }

    const loadPromise = this.loadData(language, namespace, cacheKey);
    this.loadingPromises.set(cacheKey, loadPromise);

    return loadPromise;
  }

  private async loadData(
    language: string,
    namespace: string,
    cacheKey: string
  ): Promise<any> {
    try {
      const data = await loadTranslation(language, namespace);
      this.cache.set(cacheKey, data || {});
      this.loadingPromises.delete(cacheKey);
      return data || {};
    } catch {
      const emptyData = {};
      this.cache.set(cacheKey, emptyData);
      this.loadingPromises.delete(cacheKey);
      return emptyData;
    }
  }

  clearCache() {
    this.cache.clear();
    this.loadingPromises.clear();
  }
}

const backend = new BundledTranslationBackend();

export interface SimpleI18nOptions {
  /**
   * Initial language to use. Resolve at construction time using whatever
   * platform-specific detection makes sense (navigator.language on web,
   * expo-localization on RN, etc.).
   */
  initialLanguage: string;
  /**
   * Persist a chosen language to platform storage. Called from
   * `changeLanguage` after the new translations have loaded successfully.
   * Implementations should be best-effort (no throw on failure).
   */
  persist: (language: string) => void;
}

/**
 * Platform-agnostic i18n state machine.
 *
 * No DOM access — language detection and persistence are injected via the
 * constructor options. Composers (`packages/i18n/src/web`,
 * `packages/i18n/src/mobile`) supply concrete implementations.
 */
export class SimpleI18n implements I18nInstance {
  private resources: Map<string, Map<string, any>> = new Map();
  private _language: string;
  public isLanguageChanging: boolean = false;
  public isInitialized: boolean = false;
  private changeCallbacks: Set<() => void> = new Set();
  private persistLanguage: (language: string) => void;

  constructor(options: SimpleI18nOptions) {
    const initial = options.initialLanguage;
    this._language = isLanguageSupported(initial as SupportedLanguage)
      ? (initial as SupportedLanguage)
      : DEFAULT_LANGUAGE;
    this.persistLanguage = options.persist;
  }

  get language(): string {
    return this._language;
  }

  t: TranslationFunction = (key: string, options?: any): string => {
    const [namespace, ...keyParts] = key.split(':');
    const actualKey = keyParts.join(':') || namespace;
    const ns = keyParts.length > 0 ? namespace : 'common';

    let value = this.getTranslation(this._language, ns, actualKey, options);

    if (!value && this._language !== FALLBACK_LANGUAGE) {
      value = this.getTranslation(FALLBACK_LANGUAGE, ns, actualKey, options);
    }

    if (!value) {
      if (options && options.defaultValue) {
        value = options.defaultValue;
      } else {
        const fullKey = `${ns}:${actualKey}`;
        const isDevelopment = process.env.NODE_ENV === 'development';

        if (isDevelopment) {
          // Optional dev sink, populated by tooling on web. Mobile no-ops.
          if (
            typeof globalThis !== 'undefined' &&
            (globalThis as any).__reportMissingTranslation
          ) {
            (globalThis as any).__reportMissingTranslation(fullKey);
          }
        }

        value = actualKey;
      }
    }

    if (options && typeof value === 'string') {
      return value.replace(
        /{{\s*(\w+)\s*(?:,\s*(\w+)\s*)?}}/g,
        (_match, k, format) => {
          const replacement = options[k];
          if (replacement === undefined) return _match;
          if (format === 'number' && typeof replacement === 'number') {
            return new Intl.NumberFormat(this._language).format(replacement);
          }
          return replacement;
        }
      );
    }

    return value || key;
  };

  private getTranslation(
    language: string,
    namespace: string,
    key: string,
    options?: any
  ): string | undefined {
    const langResources = this.resources.get(language);
    if (!langResources) return undefined;

    const nsResources = langResources.get(namespace);
    if (!nsResources) return undefined;

    const keys = key.split('.');
    let value: any = nsResources;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return undefined;
      }
    }

    if (options && typeof options.count === 'number') {
      const count = options.count;

      if (count === 1) {
        const singularKey = key + '_one';
        const singularValue = this.getSimpleTranslation(
          nsResources,
          singularKey
        );
        if (singularValue) {
          return singularValue;
        }
      }

      if (count === 0) {
        const zeroKey = key + '_zero';
        const zeroValue = this.getSimpleTranslation(nsResources, zeroKey);
        if (zeroValue) {
          return zeroValue;
        }
      }

      if (count !== 1) {
        const pluralKey = key + '_plural';
        const pluralValue = this.getSimpleTranslation(nsResources, pluralKey);
        if (pluralValue) {
          return pluralValue;
        }
      }
    }

    return typeof value === 'string' ? value : undefined;
  }

  private getSimpleTranslation(
    nsResources: any,
    key: string
  ): string | undefined {
    const keys = key.split('.');
    let value: any = nsResources;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return undefined;
      }
    }

    return typeof value === 'string' ? value : undefined;
  }

  async changeLanguage(lng: string): Promise<void> {
    if (!isLanguageSupported(lng as SupportedLanguage)) {
      return;
    }

    if (this._language === lng) return;

    this.isLanguageChanging = true;

    const namespacesToReload: string[] = [];
    this.resources.forEach(nsMap => {
      nsMap.forEach((_, ns) => {
        if (!namespacesToReload.includes(ns)) {
          namespacesToReload.push(ns);
        }
      });
    });

    if (!namespacesToReload.includes('common')) {
      namespacesToReload.push('common');
    }
    if (!namespacesToReload.includes('errors')) {
      namespacesToReload.push('errors');
    }

    backend.clearCache();

    const loadedTranslations: Array<{ ns: string; data: any }> = [];
    const loadPromises = namespacesToReload.map(async ns => {
      const data = await backend.read(lng, ns);
      if (data && Object.keys(data).length > 0) {
        loadedTranslations.push({ ns, data });
      }
    });
    await Promise.all(loadPromises);

    this._language = lng as SupportedLanguage;

    // Delegate persistence to the platform-supplied callback.
    try {
      this.persistLanguage(lng);
    } catch {
      /* persist is best-effort */
    }

    this.resources.clear();
    for (const { ns, data } of loadedTranslations) {
      this.addResourceBundle(lng, ns, data);
    }

    this.isLanguageChanging = false;

    this.changeCallbacks.forEach(callback => callback());
  }

  addResourceBundle(lng: string, ns: string, resources: any): void {
    if (!this.resources.has(lng)) {
      this.resources.set(lng, new Map());
    }

    const langResources = this.resources.get(lng)!;
    langResources.set(ns, resources);
  }

  getResourceBundle(lng: string, ns: string): any {
    const langResources = this.resources.get(lng);
    if (!langResources) return undefined;
    return langResources.get(ns);
  }

  hasResourceBundle(lng: string, ns: string): boolean {
    const langResources = this.resources.get(lng);
    if (!langResources) return false;
    return langResources.has(ns);
  }

  async loadNamespaces(namespaces: string | string[]): Promise<void> {
    const nsArray = Array.isArray(namespaces) ? namespaces : [namespaces];

    const loadPromises = nsArray.map(async ns => {
      if (this.hasResourceBundle(this._language, ns)) {
        return;
      }

      const data = await backend.read(this._language, ns);
      if (data && Object.keys(data).length > 0) {
        this.addResourceBundle(this._language, ns, data);
      }

      if (
        this._language !== FALLBACK_LANGUAGE &&
        !this.hasResourceBundle(FALLBACK_LANGUAGE, ns)
      ) {
        const primaryData = this.resources.get(this._language)?.get(ns);
        const needsFallback =
          !primaryData || Object.keys(primaryData).length === 0;

        if (needsFallback) {
          const fallbackData = await backend.read(FALLBACK_LANGUAGE, ns);
          if (fallbackData && Object.keys(fallbackData).length > 0) {
            this.addResourceBundle(FALLBACK_LANGUAGE, ns, fallbackData);
          }
        }
      }
    });

    await Promise.all(loadPromises);
  }

  async reloadResources(): Promise<void> {
    backend.clearCache();
    this.resources.clear();
    await this.loadNamespaces(['common']);
  }

  onChange(callback: () => void): () => void {
    this.changeCallbacks.add(callback);
    return () => this.changeCallbacks.delete(callback);
  }
}
